import { useCallback, useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";
import { subjectOptions } from "@/data/panels";

type Mode = "work" | "short" | "long";

const DURATIONS: Record<Mode, number> = {
  work: 25 * 60,
  short: 5 * 60,
  long: 15 * 60,
};

const MODE_LABELS: Record<Mode, { label: string; icon: string }> = {
  work: { label: "Odaklan", icon: "ri-focus-3-line" },
  short: { label: "Kısa Mola", icon: "ri-cup-line" },
  long: { label: "Uzun Mola", icon: "ri-rest-time-line" },
};

function formatTime(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export default function PomodoroPage() {
  const [mode, setMode] = useState<Mode>("work");
  const [secondsLeft, setSecondsLeft] = useState<number>(DURATIONS.work);
  const [running, setRunning] = useState(false);
  const [subject, setSubject] = useState<string>(subjectOptions[0]);
  const [todayCount, setTodayCount] = useState(0);
  const [todayMinutes, setTodayMinutes] = useState(0);
  const [saving, setSaving] = useState(false);

  const loadToday = useCallback(async () => {
    try {
      const dataClient = getDataClient();
      const { data: authData } = await dataClient.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) return;
      const { data, error } = await dataClient
        .from("study_sessions")
        .select("minutes")
        .eq("user_id", userId)
        .eq("studied_on", todayIso());
      if (error) return;
      const sessions = (data ?? []) as Array<{ minutes: number }>;
      setTodayCount(sessions.length);
      setTodayMinutes(sessions.reduce((sum, s) => sum + (s.minutes ?? 0), 0));
    } catch {
      // best-effort
    }
  }, []);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  // Zamanlayıcı
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  const saveWorkSession = useCallback(async () => {
    setSaving(true);
    try {
      const dataClient = getDataClient();
      const { data: authData } = await dataClient.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) return;
      await dataClient.from("study_sessions").insert({
        user_id: userId,
        subject,
        minutes: DURATIONS.work / 60,
        studied_on: todayIso(),
      });
      await loadToday();
    } finally {
      setSaving(false);
    }
  }, [subject, loadToday]);

  // Süre dolduğunda
  useEffect(() => {
    if (secondsLeft !== 0 || !running) return;
    setRunning(false);
    if (mode === "work") {
      saveWorkSession();
      setMode("short");
      setSecondsLeft(DURATIONS.short);
    } else {
      setMode("work");
      setSecondsLeft(DURATIONS.work);
    }
  }, [secondsLeft, running, mode, saveWorkSession]);

  const switchMode = (m: Mode) => {
    setMode(m);
    setSecondsLeft(DURATIONS[m]);
    setRunning(false);
  };

  const toggle = () => {
    if (secondsLeft === 0) {
      setSecondsLeft(DURATIONS[mode]);
    }
    setRunning((r) => !r);
  };

  const reset = () => {
    setRunning(false);
    setSecondsLeft(DURATIONS[mode]);
  };

  const progress =
    mode === "work"
      ? ((DURATIONS.work - secondsLeft) / DURATIONS.work) * 100
      : ((DURATIONS[mode] - secondsLeft) / DURATIONS[mode]) * 100;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
          Odaklanma Zamanlayıcısı
        </h1>
        <p className="text-sm md:text-base text-foreground-600 mt-1">
          Pomodoro tekniğiyle çalış — 25 dakika odaklan, sonra kısa mola ver. Çalışma süren otomatik kaydedilir.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Zamanlayıcı */}
        <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 flex flex-col items-center">
          {/* Mod seçici */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
            {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  mode === m
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {MODE_LABELS[m].label}
              </button>
            ))}
          </div>

          {/* Büyük sayaç */}
          <div className="mt-10 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className={`${MODE_LABELS[mode].icon} text-3xl`} aria-hidden="true"></i>
            </span>
            <p className="mt-6 font-heading text-6xl md:text-7xl font-bold text-foreground-950 tabular-nums">
              {formatTime(secondsLeft)}
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              {mode === "work" ? "Çalışma süresi" : "Mola süresi"}
            </p>
          </div>

          {/* Progress */}
          <div className="mt-8 w-full max-w-sm h-2 rounded-full bg-background-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                mode === "work" ? "bg-primary-500" : "bg-secondary-500"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Kontroller */}
          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={toggle}
              className="whitespace-nowrap px-8 py-3 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors"
            >
              {running ? "Durdur" : "Başlat"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="whitespace-nowrap px-6 py-3 rounded-md border border-background-200 text-foreground-700 font-semibold hover:bg-background-100 transition-colors"
            >
              Sıfırla
            </button>
          </div>

          {/* Ders seçimi */}
          {mode === "work" && (
            <div className="mt-8 w-full max-w-sm">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Hangi dersi çalışıyorsun?
              </label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                {subjectOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          {saving && (
            <p className="mt-4 text-xs text-foreground-500 flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
              Çalışma süresi kaydediliyor…
            </p>
          )}
        </div>

        {/* Bugünün özeti */}
        <div className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading font-semibold text-base text-foreground-950">
              Bugünkü Odaklanma
            </h3>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="rounded-md bg-primary-50 p-4">
                <p className="text-2xl font-heading font-bold text-primary-700">{todayCount}</p>
                <p className="mt-1 text-xs text-foreground-600">odaklanma seansı</p>
              </div>
              <div className="rounded-md bg-accent-50 p-4">
                <p className="text-2xl font-heading font-bold text-accent-700">{todayMinutes}</p>
                <p className="mt-1 text-xs text-foreground-600">dakika çalışma</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading font-semibold text-base text-foreground-950">
              Nasıl çalışır?
            </h3>
            <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
              <li className="flex gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs shrink-0">1</span>
                25 dakika boyunca tek konuya odaklan.
              </li>
              <li className="flex gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs shrink-0">2</span>
                Telefonu ve dikkat dağıtan şeyleri uzaklaştır.
              </li>
              <li className="flex gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs shrink-0">3</span>
                Süre dolunca 5 dakika mola ver.
              </li>
              <li className="flex gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs shrink-0">4</span>
                4 seanstan sonra 15 dakika uzun mola ver.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}